import * as React from "react";
import { FlaskConical, Pause, Play, Square } from "lucide-react";
import { toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import { useStartSandbox } from "@/features/simulations/sandbox/hooks/useStartSandbox";
import { Pill, type PillTone } from "@/components/ui/pill";
import { useActiveRun, usePauseRun, useResumeRun } from "@/features/production/hooks";
import { FinishProductionDialog } from "@/features/production/FinishProductionDialog";
import { PauseRunDialog } from "@/features/production/PauseRunDialog";
import { ProductionOrderFormDialog } from "@/features/production-orders/ProductionOrderFormDialog";
import { useAuthStore } from "@/stores/auth.store";
import { useLine, type LineSummary } from "@/features/lines/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { LineKPIStripPeriodFilter } from "./LineKPIStripPeriodFilter";

export interface LineDetailHeaderProps {
  lineId: string;
}

const STATUS_PILL: Record<
  LineSummary["status"],
  { tone: PillTone; label: string; pulse: boolean }
> = {
  RUNNING: { tone: "success", label: "Em produção", pulse: true },
  STOPPED: { tone: "danger", label: "Parada", pulse: true },
  PARTIAL: { tone: "warning", label: "Pausada", pulse: false },
  IDLE: { tone: "neutral", label: "Ociosa", pulse: false },
};

function formatDuration(fromIso: string | null | undefined): string | null {
  if (!fromIso) return null;
  const from = new Date(fromIso).getTime();
  if (Number.isNaN(from)) return null;
  const diffMin = Math.max(0, Math.floor((Date.now() - from) / 60000));
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export function LineDetailHeader({ lineId }: LineDetailHeaderProps): React.ReactElement | null {
  const { data: line } = useLine(lineId);
  const { data: run } = useActiveRun(lineId);
  const pause = usePauseRun(lineId);
  const resume = useResumeRun(lineId);
  // "Iniciar" abre o modal de Nova OP (linha travada). Criar OP é GESTOR-only (igual à tela de OP).
  const isGestor = useAuthStore((s) => s.user?.role === "GESTOR");

  const startSandbox = useStartSandbox();
  const [showStart, setShowStart] = React.useState(false);
  const [showPause, setShowPause] = React.useState(false);
  const [showFinish, setShowFinish] = React.useState(false);

  if (!line) return null;

  const pill = STATUS_PILL[line.status] ?? STATUS_PILL.IDLE;
  const durationSource =
    line.status === "RUNNING" ? run?.startedAt : line.status === "PARTIAL" ? run?.pausedAt : null;
  const duration = formatDuration(durationSource);

  const isActive = run?.status === "ACTIVE";
  const isPaused = run?.status === "PAUSED";
  const productName = run?.products?.[0]?.product.name ?? null;

  async function onPause(opts: { observation?: string } = {}) {
    if (!run) return;
    try {
      await pause.mutateAsync({ runId: run.id, observation: opts.observation });
      toast.success("Produção pausada.");
      setShowPause(false);
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao pausar.", { description: msg });
    }
  }

  async function onResume() {
    if (!run) return;
    try {
      await resume.mutateAsync(run.id);
      toast.success("Produção retomada.");
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao retomar.", { description: msg });
    }
  }

  return (
    <header className="mt-3 flex flex-wrap items-start justify-between gap-6">
      <div className="min-w-0 flex-1">
        {}
        <div className="flex flex-wrap items-center gap-3.5">
          <h1 className="text-[32px] font-semibold leading-[1.05] tracking-[-0.03em] text-text-primary">
            {line.name}
          </h1>
          <Pill tone={pill.tone} withDot pulse={pill.pulse} className="text-[12.5px]">
            {pill.label}
            {duration ? (
              <span className="ml-1.5 font-mono text-text-tertiary">· {duration}</span>
            ) : null}
          </Pill>
        </div>

        {}
        {line.description || line.responsible || productName ? (
          <p className="mt-2 flex flex-wrap items-center gap-2.5 text-[13.5px] text-text-secondary">
            {line.description ? <span>{line.description}</span> : null}
            {line.description && (line.responsible || productName) ? (
              <span className="h-[3px] w-[3px] rounded-full bg-text-tertiary" aria-hidden="true" />
            ) : null}
            {line.responsible ? (
              <span>
                Responsável: <b className="text-text-primary">{line.responsible}</b>
              </span>
            ) : null}
            {line.responsible && productName ? (
              <span className="h-[3px] w-[3px] rounded-full bg-text-tertiary" aria-hidden="true" />
            ) : null}
            {productName ? (
              <span>
                Produzindo <b className="text-text-primary">{productName}</b>
              </span>
            ) : null}
          </p>
        ) : null}

        {}
        <div className="mt-[18px]">
          <LineKPIStripPeriodFilter />
        </div>
      </div>

      {}
      <div className="flex flex-wrap items-center gap-2">
        {isGestor ? (
          <Button size="sm" variant="outline" onClick={() => void startSandbox(lineId, line.name)}>
            <FlaskConical className="h-4 w-4" />
            Simular esta linha
          </Button>
        ) : null}
        {!run && isGestor ? (
          <Button size="sm" variant="primary" onClick={() => setShowStart(true)}>
            <Play className="h-4 w-4" />
            Iniciar
          </Button>
        ) : null}
        {run && isActive ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowPause(true)}
            disabled={pause.isPending}
          >
            <Pause className="h-4 w-4" />
            Pausar
          </Button>
        ) : null}
        {run && isPaused ? (
          <Button
            size="sm"
            variant="primary"
            onClick={() => void onResume()}
            disabled={resume.isPending}
          >
            <Play className="h-4 w-4" />
            Retomar
          </Button>
        ) : null}
        {run ? (
          <Button size="sm" variant="danger" onClick={() => setShowFinish(true)}>
            <Square className="h-4 w-4" />
            Finalizar
          </Button>
        ) : null}
      </div>

      {showStart ? (
        <ProductionOrderFormDialog
          mode="create"
          lockedLineId={lineId}
          onClose={() => setShowStart(false)}
        />
      ) : null}

      {run ? (
        <FinishProductionDialog
          lineId={lineId}
          runId={run.id}
          producedQty={run.producedQty}
          open={showFinish}
          onClose={() => setShowFinish(false)}
        />
      ) : null}

      <PauseRunDialog
        open={showPause}
        onOpenChange={setShowPause}
        onConfirm={onPause}
        pending={pause.isPending}
        inputId="hdr-pause-obs"
      />
    </header>
  );
}
